import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";

const API_END_POINT = "/group/admin/members/bulk";

export type MemberOperation = "change_role" | "delete_member";

export type MemberRole = "Admin" | "Member" | "Editor" | "Guest" | "None";

/**
 * Hook to perform bulk operations on group members
 */
export function useGroupMembersBulk() {
  const { callApi } = usePymbleApi();
  const queryClient = useQueryClient();

  const invalidateMembers = () => {
    queryClient.invalidateQueries({ queryKey: ["groupMembers"] });
  };

  const upsertMutation = useMutation({
    mutationFn: async ({
      operation,
      role,
      groups,
      contacts,
    }: {
      operation: MemberOperation;
      role: MemberRole;
      groups: string[];
      contacts: string[];
    }) => {
      const request = { operation, role, groups, contacts };
      return await callApi("POST", API_END_POINT, { data: request });
    },
    onSuccess: invalidateMembers,
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      groups,
      contacts,
    }: {
      groups: string[];
      contacts: string[];
    }) => {
      const request = { operation: "delete_member", groups, contacts };
      return await callApi("DELETE", API_END_POINT, { data: request });
    },
    onSuccess: invalidateMembers,
  });

  /**
   * Perform bulk operations on group members for a set of groups.
   */
  const upsertMembers = async (
    operation: MemberOperation,
    role: MemberRole,
    groups: string[],
    contacts: string[]
  ) => {
    return upsertMutation.mutateAsync({ operation, role, groups, contacts });
  };

  /**
   * Perform bulk delete of group members from a set of groups.
   */
  const deleteMembers = async (groups: string[], contacts: string[]) => {
    return deleteMutation.mutateAsync({ groups, contacts });
  };

  return {
    upsertMembers,
    deleteMembers,
    groupMembersBulkSaving: upsertMutation.isPending || deleteMutation.isPending,
  };
}
